import React, { useReducer, useEffect, useState, useMemo } from "react";
import { GridState, GridNode, FuelType, GridTopology, HoverElement, InteractionState } from "./types";
import { GridStateWithSolution, HourlyData } from "./powerFlow/multiTimestepOptimization";
import { BezierCurve } from "./bezier/bezier";
import GridVisualization from "./components/GridVisualization";
import ElementTable from "./components/ElementTable/index";
import LoadCurve from "./components/CurveEditors/LoadCurve";
import WindCurve from "./components/CurveEditors/WindCurve";
import SunCurve from "./components/CurveEditors/SunCurve";
import BatterySOCChart from "./components/ElementTable/BatterySOCChart";
import BusLMPSurfaceChart from "./components/ElementTable/BusLMPSurfaceChart";
import { ImportExportDialog } from "./components/ImportExportDialog";
import { Toolbar } from "./components/GridVisualization/Toolbar";
import { type ScenarioType } from "./scenarios/networks";
import { appReducer, initialState } from "./reducer";
import { getHourlyGridStates } from "./powerFlow/multiTimestepOptimization";
import { useLMPSurfaceWorker } from "./hooks/useLMPSurfaceWorker";
import {
  applyAutomaticLayout,
  applyHierarchicalLayout,
  applyForceLayout,
  findBestLayout,
} from "./utils/layoutUtils";
import { useURLState } from "./hooks/useURLState";
import { useAsyncWithDefault } from "./hooks/useAsyncWithDefault";
import { useIsMobile } from "./hooks/useIsMobile";
import MobileApp from "./components/mobile/MobileApp";
import { logger } from "./utils/logger";
import { buildData, Data, compileRuleSet, groundData } from "./powerFlow/language";
import { solveAcPowerFlow } from "./powerFlow/acDispatch";
import { lpGroundedProblem, GroundedProblem } from "./powerFlow/groundedProblem";
import { AC_BASE_CATALOG, describeAcCatalog } from "./powerFlow/nonlinear/acPowerFlow";

function App() {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [interactionState, setInteractionState] = useState<InteractionState>({
    mode: "normal",
  });
  const isMobile = useIsMobile();

  const {
    gridTopology,
    loadCurve,
    windCurve,
    solarCurve,
    hoverState,
    isLoading,
    initialLoadComplete,
    showImportExport,
    importExportText,
    activeTab,
    revealSeq,
    currentScenario,
    documentName,
    history,
    numTimeSteps,
    rules,
    acRules,
    mode,
  } = state;

  // The editable rule set, parsed and schema-checked. `compiled.program` is the
  // enabled, error-free subset — what the solver grounds — while `compiled.rules`
  // carries every rule's text and errors for the elements panel to render. The
  // catalog it checks against is derived from the rules themselves, so the
  // variable classes follow the rules that declare them.
  const dcCompiled = useMemo(() => compileRuleSet(rules), [rules]);
  const program = dcCompiled.program;
  // ACPF compiles its own editable rule set against the AC base catalog. Both
  // are kept live; the mode picks which one the elements panel shows.
  const acCompiled = useMemo(() => compileRuleSet(acRules, AC_BASE_CATALOG), [acRules]);
  // For the panel, overlay the plain-language relation descriptions (covers the
  // derived admittances too); the solve keeps using acCompiled.program's raw catalog.
  const activeCompiled = useMemo(
    () =>
      mode === "acpf"
        ? { ...acCompiled, catalog: describeAcCatalog(acCompiled.catalog) }
        : dcCompiled,
    [mode, acCompiled, dcCompiled],
  );

  // Compute hourly grid states (async)
  const { value: hourlyGridStates, error: solveError } = useAsyncWithDefault(
    async () => {
      if (!gridTopology) {
        return [];
      }

      // Convert topology to grid state by adding default values
      const initialGridState: GridState = {
        nodes: gridTopology.nodes.map(node => ({ ...node, value: 0 })),
        edges: gridTopology.edges.map(edge => ({ ...edge, current: 0 }))
      };

      // Always use multi-timestep optimization
      return await getHourlyGridStates(
        initialGridState,
        loadCurve.controlPoints,
        windCurve.controlPoints,
        solarCurve.controlPoints,
        numTimeSteps,
        state.requireMatchingSOC,
        undefined,
        0,
        program
      );
    },
    {
      defaultValue: [],
      deps: [gridTopology, loadCurve.controlPoints, windCurve.controlPoints, solarCurve.controlPoints, numTimeSteps, state.requireMatchingSOC, program],
      onError: (error) => logger.error('Error computing hourly grid states:', error)
    }
  );

  // Reset hover hour if it's beyond the current time steps range
  useEffect(() => {
    if (hoverState.hoverHour !== null && hoverState.hoverHour >= numTimeSteps) {
      dispatch({
        type: "SET_HOVER_HOUR",
        payload: Math.max(0, numTimeSteps - 1),
      });
    }
  }, [numTimeSteps, hoverState.hoverHour]);

  // Derive hourly generation data from hourly grid states (no duplicate computation)
  const hourlyGenerationData: HourlyData[] = useMemo(() => {
    return hourlyGridStates.map(({ hour, gridState }) => {
      if (gridState.solutionValid === false) {
        return { type: 'infeasible', hour };
      }
      return {
        type: 'feasible',
        hour,
        generators: gridState.nodes
          .filter(node => node.type === 'generator')
          .map(gen => ({ id: gen.id, value: gen.value }))
      };
    });
  }, [hourlyGridStates]);

  // The model's input relations (demand, gen_cap, …), from the grid topology and
  // curves — the same pipeline the interpreter grounds from.
  // These are inputs to the optimization, so the DATA-section tables render them
  // directly rather than reading them back out of the solved model.
  const inputData: Data | undefined = useMemo(() => {
    if (!gridTopology) return undefined;
    // The base relations plus whatever the definition rules compute from them,
    // so the DATA tables show a derived relation the same as any other.
    return groundData(program, buildData({
      // Match the solve path: topology nodes carry no dispatch value yet.
      nodes: gridTopology.nodes.map((node) => ({ ...node, value: 0 })),
      edges: gridTopology.edges,
      loadCurve: loadCurve.controlPoints,
      windCurve: windCurve.controlPoints,
      solarCurve: solarCurve.controlPoints,
      numHours: numTimeSteps,
    }));
  }, [gridTopology, loadCurve.controlPoints, windCurve.controlPoints, solarCurve.controlPoints, numTimeSteps, program]);

  // Get current hour's grid state and battery states
  // Clamp hover hour to available time steps to prevent crashes
  const maxAvailableHour = Math.max(0, hourlyGridStates.length - 1);
  const clampedHoverHour = hoverState.hoverHour !== null 
    ? Math.min(Math.max(0, hoverState.hoverHour), maxAvailableHour)
    : 0;
  const currentHour = clampedHoverHour;
  const currentHourState = hourlyGridStates.find(h => h.hour === currentHour);

  // ACPF is a single snapshot solved synchronously from the topology + its AC
  // overrides (fast — no worker, no time coupling), recomputed only when the
  // mode or topology changes.
  const acResult = useMemo(() => {
    if (mode !== "acpf" || !gridTopology) return null;
    const grid: GridState = {
      nodes: gridTopology.nodes.map((n) => ({ ...n, value: 0 })),
      edges: gridTopology.edges.map((e) => ({ ...e, current: 0 })),
    };
    return solveAcPowerFlow(grid, acCompiled.program);
  }, [mode, gridTopology, acCompiled]);

  // The grid shown (and its solution) follows the mode: the AC snapshot, or the
  // DCOPF solution for the hovered hour.
  const gridState =
    mode === "acpf" ? acResult?.gridState ?? null : currentHourState?.gridState || null;
  const batteryStates = currentHourState?.batteryStates;

  // The solved problem in backend-neutral form for the elements panel — the AC
  // Newton result, or the DCOPF LP model + solution adapted to the same shape.
  const problem: GroundedProblem | undefined = useMemo(() => {
    if (mode === "acpf") return acResult?.problem;
    const model = gridState?.models ? Array.from(gridState.models.values())[0] : undefined;
    if (!model) return undefined;
    const solution = gridState?.lpSolutions
      ? Array.from(gridState.lpSolutions.values())[0]
      : undefined;
    return lpGroundedProblem(
      model,
      solution,
      gridState?.solutionValid !== false,
      gridState?.solutionMessage ?? "",
    );
  }, [mode, acResult, gridState]);

  // --- Selection ---
  // The globally selected element (node or edge) lives in the reducer,
  // alongside the hovered element. The LMP curve reacts to a selected node.
  const { selectedElement } = state;
  const selectedBusId =
    selectedElement?.type === "Node" ? selectedElement.id : null;

  const handleSelectNode = (nodeId: string) => {
    dispatch({ type: "SET_SELECTED_ELEMENT", payload: { type: "Node", id: nodeId } });
  };
  const clearSelection = () => {
    dispatch({ type: "SET_SELECTED_ELEMENT", payload: null });
  };

  // Clear the selection if the selected element no longer exists.
  useEffect(() => {
    if (!selectedElement || !gridTopology) return;
    const exists =
      selectedElement.type === "Node"
        ? gridTopology.nodes.some(n => n.id === selectedElement.id)
        : gridTopology.edges.some(e => e.id === selectedElement.id);
    if (!exists) {
      dispatch({ type: "SET_SELECTED_ELEMENT", payload: null });
    }
  }, [selectedElement, gridTopology]);

  // Compute the LMP surface at the selected bus (LMP-vs-demand staircase at
  // every timestep, colored by price) in a web worker so the synchronous WASM
  // solves don't block the UI. Covers all hours, so it does not depend on the
  // hovered hour.
  const { result: lmpSurface, loading: lmpSurfaceLoading } = useLMPSurfaceWorker({
    busId: selectedBusId,
    gridTopology,
    loadCurve: loadCurve.controlPoints,
    windCurve: windCurve.controlPoints,
    solarCurve: solarCurve.controlPoints,
    numTimeSteps,
    requireMatchingSOC: state.requireMatchingSOC,
    program,
  });

  // Log solution for current hour (for debugging)
  useEffect(() => {
    if (gridState && gridState.models && gridState.models.size > 0) {
      gridState.models.forEach((model, componentId: number) => {
        logger.debug(`\n=== Model for Hour ${currentHour}, Component ${componentId} ===`);
        logger.debug(`Variables: ${model.variables.length}`);
        logger.debug(`Constraints: ${model.constraints.length}`);
        logger.debug(`Objective: ${model.objective.sense}`);
      });
      
      if (gridState.lpSolutions && gridState.lpSolutions.size > 0) {
        logger.debug(`=== LP Solution for Hour ${currentHour} ===`);
        gridState.lpSolutions.forEach((lpSolution, componentId: number) => {
          logger.debug(`Component ${componentId}:`);
          logger.debug('Status:', lpSolution.Status);
          logger.debug('Objective Value:', lpSolution.ObjectiveValue);
          logger.debug('Variables:');
          Object.entries(lpSolution.Columns || {}).forEach(([varName, varData]: [string, any]) => {
            logger.debug(`  ${varName}: ${varData.Primal}`);
          });
        });
        logger.debug('=== End LP Solution ===\n');
      }
    }
  }, [currentHour, gridState?.models, gridState?.lpSolutions]);

  // Keyboard shortcuts for undo/redo (⌘/Ctrl+Z, ⌘/Ctrl+Shift+Z or Ctrl+Y).
  // Ignored while typing in a form field so text inputs keep their own undo.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;

      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) {
        return;
      }

      const key = event.key.toLowerCase();
      if (key === "z") {
        event.preventDefault();
        dispatch({ type: event.shiftKey ? "REDO" : "UNDO" });
      } else if (key === "y") {
        event.preventDefault();
        dispatch({ type: "REDO" });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // URL persistence utilities
  const { updateUrl, parseUrlState } = useURLState();

  // Load initial scenario on component mount
  useEffect(() => {
    const loadSystem = async () => {
      try {
        const urlState = parseUrlState();
        dispatch({
          type: "LOAD_SCENARIO",
          payload: {
            scenario: urlState.scenario,
            gridTopology: urlState.gridTopology,
          },
        });
        // Set active tab from URL if present
        if (urlState.activeTab) {
          dispatch({
            type: "SET_ACTIVE_TAB",
            payload: urlState.activeTab,
          });
        }
        // Set curves from URL if present
        if (urlState.loadCurve) {
          dispatch({
            type: "UPDATE_LOAD_CURVE",
            payload: urlState.loadCurve,
          });
        }
        if (urlState.windCurve) {
          dispatch({
            type: "UPDATE_WIND_CURVE",
            payload: urlState.windCurve,
          });
        }
        if (urlState.solarCurve) {
          dispatch({
            type: "UPDATE_SOLAR_CURVE",
            payload: urlState.solarCurve,
          });
        }
        // Restore the selected element from the URL if present.
        if (urlState.selectedElement) {
          dispatch({
            type: "SET_SELECTED_ELEMENT",
            payload: urlState.selectedElement,
          });
        }
      } catch (error) {
        logger.error("Failed to load scenario:", error);
        // Fallback to empty system
        dispatch({ type: "SET_GRID_TOPOLOGY", payload: { nodes: [], edges: [] } });
        dispatch({ type: "SET_LOADING", payload: false });
        dispatch({ type: "SET_INITIAL_LOAD_COMPLETE", payload: true });
      }
    };

    loadSystem();
  }, []); // Only run once on mount

  // Handle scenario changes (separate from initial load)
  // Only reload scenario when explicitly changing scenarios, not on every state change
  const handleScenarioChange = (newScenario: ScenarioType) => {
    if (newScenario !== currentScenario) {
      dispatch({
        type: "LOAD_SCENARIO",
        payload: { scenario: newScenario },
      });

      // Update URL to reflect scenario change. Selection is cleared when
      // switching scenarios (the old element no longer exists), so omit it.
      updateUrl(newScenario, undefined, activeTab, loadCurve.controlPoints, windCurve.controlPoints, solarCurve.controlPoints, null);
    }
  };

  // Update URL when grid topology, activeTab, curves, or selection change
  useEffect(() => {
    if (initialLoadComplete && gridTopology && currentScenario) {
      updateUrl(currentScenario, gridTopology, activeTab, loadCurve.controlPoints, windCurve.controlPoints, solarCurve.controlPoints, selectedElement);
    }
  }, [gridTopology, currentScenario, activeTab, loadCurve.controlPoints, windCurve.controlPoints, solarCurve.controlPoints, selectedElement, initialLoadComplete]);

  // Note: Node values are now computed from dispatch, not directly set

  const handleNodePositionChange = (nodeId: string, x: number, y: number) => {
    dispatch({
      type: "UPDATE_NODE_POSITION",
      payload: { nodeId, x, y },
    });
  };

  const handleNodeCostChange = (nodeId: string, cost: number) => {
    dispatch({
      type: "UPDATE_NODE_COST",
      payload: { nodeId, cost },
    });
  };

  const handleNodeFuelTypeChange = (nodeId: string, fuelType: FuelType) => {
    dispatch({
      type: "UPDATE_NODE_FUEL_TYPE",
      payload: { nodeId, fuelType },
    });
  };

  const handleBatteryParametersChange = (
    batteryId: string,
    params: {
      maxChargeMW?: number;
      maxDischargeMW?: number;
      maxEnergyMWh?: number;
      minEnergyMWh?: number;
      efficiency?: number;
      initialEnergyMWh?: number;
    }
  ) => {
    dispatch({
      type: "UPDATE_BATTERY_PARAMETERS",
      payload: { batteryId, params },
    });
  };

  const handleEdgeRatingChange = (edgeId: string, rating: number) => {
    dispatch({
      type: "UPDATE_EDGE_RATING",
      payload: { edgeId, rating },
    });
  };

  const handleAddNode = (type: GridNode["type"], x: number, y: number) => {
    const nodeId = `${type}${state.nodeCounter}`;
    dispatch({
      type: "ADD_NODE",
      payload: { type, x, y },
    });
    setInteractionState({ mode: "adding-edge", selectedNodeForEdge: nodeId });
  };

  const handleRemoveNode = (nodeId: string) => {
    dispatch({
      type: "REMOVE_NODE",
      payload: nodeId,
    });
  };

  const handleAddEdge = (fromNodeId: string, toNodeId: string) => {
    dispatch({
      type: "ADD_EDGE",
      payload: { fromNodeId, toNodeId },
    });
  };

  const handleRemoveEdge = (edgeId: string) => {
    dispatch({
      type: "REMOVE_EDGE",
      payload: edgeId,
    });
  };

  const handleRevealInPanel = (nodeId: string) => {
    dispatch({
      type: "REVEAL_IN_PANEL",
      payload: nodeId,
    });
  };

  const handleLoadCurveChange = (controlPoints: BezierCurve) => {
    dispatch({
      type: "UPDATE_LOAD_CURVE",
      payload: controlPoints,
    });
  };

  const handleWindCurveChange = (controlPoints: BezierCurve) => {
    dispatch({
      type: "UPDATE_WIND_CURVE",
      payload: controlPoints,
    });
  };

  const handleSolarCurveChange = (controlPoints: BezierCurve) => {
    dispatch({
      type: "UPDATE_SOLAR_CURVE",
      payload: controlPoints,
    });
  };

  const handleHourHover = (hour: number | null) => {
    // Clamp hour to valid range based on current time steps
    let clampedHour = hour;
    if (hour !== null) {
      const maxHour = Math.max(0, numTimeSteps - 1);
      clampedHour = Math.min(Math.max(0, hour), maxHour);
    }
    
    dispatch({
      type: "SET_HOVER_HOUR",
      payload: clampedHour,
    });
  };

  const handleElementHover = (element: HoverElement | null) => {
    dispatch({
      type: "SET_HOVER_ELEMENT",
      payload: element,
    });
  };

  const handleLayoutChange = (newGridState: GridState) => {
    // Convert GridState to GridTopology since we only store topology in the state
    const newTopology: GridTopology = {
      nodes: newGridState.nodes.map(node => ({
        id: node.id,
        type: node.type,
        x: node.x,
        y: node.y,
        minPowerMW: node.minPowerMW,
        maxPowerMW: node.maxPowerMW,
        costPerMWh: node.costPerMWh,
        fuelType: node.fuelType
      })),
      edges: newGridState.edges.map(edge => ({
        id: edge.id,
        from: edge.from,
        to: edge.to,
        resistance: edge.resistance,
        rating: edge.rating
      }))
    };
    
    dispatch({
      type: "HANDLE_LAYOUT_CHANGE",
      payload: newTopology,
    });
  };

  const handleExport = () => {
    if (!gridState) return;

    const exportData = {
      ...gridState,
      metadata: {
        exportedAt: new Date().toISOString(),
        version: "1.0",
      },
    };
    dispatch({
      type: "SET_IMPORT_EXPORT_TEXT",
      payload: JSON.stringify(exportData, null, 2),
    });
    dispatch({ type: "SET_SHOW_IMPORT_EXPORT", payload: true });
  };

  const handleNewScenario = () => {
    dispatch({ type: "NEW_SCENARIO" });
    dispatch({ type: "SET_SELECTED_ELEMENT", payload: null });
    setInteractionState({ mode: "normal" });
  };

  const handleUndo = () => dispatch({ type: "UNDO" });
  const handleRedo = () => dispatch({ type: "REDO" });
  // Commit the current live topology to the undo history as one step. Fired
  // when a continuous gesture finishes — a canvas drag ending, or a table
  // input losing focus — so the whole gesture is a single undo step.
  const handleCommitEdit = () => dispatch({ type: "COMMIT_TOPOLOGY" });

  const handleImport = () => {
    try {
      const data = JSON.parse(importExportText);

      // Validate the structure
      if (
        !data.nodes ||
        !data.edges ||
        !Array.isArray(data.nodes) ||
        !Array.isArray(data.edges)
      ) {
        alert("Invalid JSON format. Expected nodes and edges arrays.");
        return;
      }

      // Validate nodes
      for (const node of data.nodes) {
        if (
          !node.id ||
          !node.type ||
          typeof node.x !== "number" ||
          typeof node.y !== "number" ||
          typeof node.value !== "number"
        ) {
          alert(
            "Invalid node format. Each node must have id, type, x, y, and value."
          );
          return;
        }
      }

      // Validate edges
      for (const edge of data.edges) {
        if (
          !edge.id ||
          !edge.from ||
          !edge.to ||
          typeof edge.resistance !== "number" ||
          typeof edge.rating !== "number"
        ) {
          alert(
            "Invalid edge format. Each edge must have id, from, to, resistance, and rating."
          );
          return;
        }
      }

      dispatch({
        type: "IMPORT_GRID_TOPOLOGY",
        payload: { nodes: data.nodes, edges: data.edges },
      });
    } catch (error) {
      alert("Invalid JSON format: " + (error as Error).message);
    }
  };

  // Layout handlers
  const handleOptimizeLayout = () => {
    if (!gridState) return;
    const result = findBestLayout(gridState);
    handleLayoutChange(result.layout);
  };

  const handleApplyLayout = (
    layoutType: "hierarchical" | "force" | "topdown" | "leftright"
  ) => {
    if (!gridState) return;

    let newLayout: GridState;
    if (layoutType === "topdown" || layoutType === "leftright") {
      newLayout = applyAutomaticLayout(gridState, {
        rankdir: layoutType === "leftright" ? "LR" : "TB",
      });
    } else if (layoutType === "hierarchical") {
      newLayout = applyHierarchicalLayout(gridState);
    } else {
      newLayout = applyForceLayout(gridState);
    }

    handleLayoutChange(newLayout);
  };

  // Removed redundant useEffect - all handlers already call solvePowerFlow
  // This was causing race conditions where dispatch results were overwritten

  // Show loading indicator while the scenario is loading or the initial
  // solution is still being computed. `gridState` is derived asynchronously
  // from the solver, so it's briefly null after the topology loads but before
  // the first solve completes — that's a loading state, not a failure.
  if (isLoading || (!gridState && !solveError)) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "18px",
              marginBottom: "10px",
              color: "#333",
            }}
          >
            Loading Grid System...
          </div>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #3498db",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto",
            }}
          />
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Only reached when the solver genuinely errored (the loading branch above
  // already covers the case where the solution is still being computed).
  if (!gridState) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div>Failed to load grid system.</div>
      </div>
    );
  }

  // The four panels are built once and shared by both layouts: the desktop 2×2
  // grid and the mobile drill-down. This keeps every handler wired identically
  // regardless of which layout is showing.
  const toolbarEl = (
    <Toolbar
      interactionState={interactionState}
      setInteractionState={setInteractionState}
      onExport={handleExport}
      onImport={() => {
        dispatch({ type: "SET_IMPORT_EXPORT_TEXT", payload: "" });
        dispatch({ type: "SET_SHOW_IMPORT_EXPORT", payload: true });
      }}
      onNewScenario={handleNewScenario}
      onUndo={handleUndo}
      onRedo={handleRedo}
      canUndo={history.canUndo}
      canRedo={history.canRedo}
      currentScenario={currentScenario}
      documentName={documentName}
      onScenarioChange={handleScenarioChange}
      mode={mode}
      onModeChange={(m) => dispatch({ type: "SET_MODE", payload: m })}
    />
  );

  const networkEl = (
    <GridVisualization
      gridState={gridState}
      onNodePositionChange={handleNodePositionChange}
      onEdgeRatingChange={handleEdgeRatingChange}
      onAddNode={handleAddNode}
      onRemoveNode={handleRemoveNode}
      onAddEdge={handleAddEdge}
      onRemoveEdge={handleRemoveEdge}
      onRevealInPanel={handleRevealInPanel}
      onInteractionEnd={handleCommitEdit}
      onOptimizeLayout={handleOptimizeLayout}
      interactionState={interactionState}
      setInteractionState={setInteractionState}
      hoverElement={hoverState.element}
      onElementHover={handleElementHover}
      batteryStates={batteryStates}
      selectedBusId={selectedBusId}
      onBusSelect={handleSelectNode}
    />
  );

  const chartsEl = (
    <>
      <div style={{ height: "320px", flexShrink: 0, borderBottom: "1px solid #ddd" }}>
        <LoadCurve
          controlPoints={loadCurve.controlPoints}
          onControlPointsChange={handleLoadCurveChange}
          gridState={gridState}
          onHourHover={handleHourHover}
          hoverHour={hoverState.hoverHour}
          hourlyData={hourlyGenerationData}
          hourlyGridStates={hourlyGridStates}
          hoverElement={hoverState.element}
          onElementHover={handleElementHover}
        />
      </div>
      <div style={{ height: "200px", flexShrink: 0, padding: "8px", borderBottom: "1px solid #ddd" }}>
        <BatterySOCChart
          batteries={gridState.nodes.filter(node => node.type === "battery")}
          hourlyData={hourlyGridStates}
          numTimeSteps={numTimeSteps}
          hoverHour={hoverState.hoverHour}
          onHourHover={handleHourHover}
        />
      </div>
      <div style={{ height: "260px", flexShrink: 0, padding: "8px" }}>
        <BusLMPSurfaceChart
          busId={selectedBusId}
          numTimeSteps={numTimeSteps}
          result={lmpSurface}
          loading={lmpSurfaceLoading}
          onClear={clearSelection}
          hoverHour={hoverState.hoverHour}
          onHourHover={handleHourHover}
        />
      </div>
    </>
  );

  const renewablesEl = (
    <>
      <div style={{ flex: 1, borderBottom: "1px solid #ddd", minHeight: 0 }}>
        <WindCurve
          controlPoints={windCurve.controlPoints}
          onControlPointsChange={handleWindCurveChange}
          onHourHover={handleHourHover}
          hoverHour={hoverState.hoverHour}
          width={undefined}
          height={undefined}
        />
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <SunCurve
          controlPoints={solarCurve.controlPoints}
          onControlPointsChange={handleSolarCurveChange}
          onHourHover={handleHourHover}
          hoverHour={hoverState.hoverHour}
          width={undefined}
          height={undefined}
        />
      </div>
    </>
  );

  const renderElementTable = (mobile: boolean, onExit?: () => void) => (
    <ElementTable
      gridState={gridState}
      mode={mode}
      problem={problem}
      // The compiled set (and its catalog) already follows the mode, so the
      // panel renders AC rules/relations from `rules` directly; it just needs the
      // AC residual system for the graph and the AC input relations to tabulate.
      residualSystem={mode === "acpf" ? acResult?.system : undefined}
      baseCatalog={mode === "acpf" ? AC_BASE_CATALOG : undefined}
      inputData={mode === "acpf" ? acResult?.data : inputData}
      rules={activeCompiled}
      onRuleSourceChange={(id, source) =>
        dispatch({ type: "SET_RULE_SOURCE", payload: { id, source } })
      }
      onRuleEnabledChange={(id, enabled) =>
        dispatch({ type: "SET_RULE_ENABLED", payload: { id, enabled } })
      }
      onRuleAdd={() => dispatch({ type: "ADD_RULE" })}
      onRuleRemove={(id) => dispatch({ type: "REMOVE_RULE", payload: id })}
      onCostSourceChange={(id, source) =>
        dispatch({ type: "SET_COST_SOURCE", payload: { id, source } })
      }
      onCostEnabledChange={(id, enabled) =>
        dispatch({ type: "SET_COST_ENABLED", payload: { id, enabled } })
      }
      onCostAdd={() => dispatch({ type: "ADD_COST" })}
      onCostRemove={(id) => dispatch({ type: "REMOVE_COST", payload: id })}
      onRulesReset={() => dispatch({ type: "RESET_RULES" })}
      batteryStates={batteryStates}
      hourlyGridStates={hourlyGridStates}
      onNodeCostChange={handleNodeCostChange}
      onNodeFuelTypeChange={handleNodeFuelTypeChange}
      onBatteryParametersChange={handleBatteryParametersChange}
      onCommitEdit={handleCommitEdit}
      requireMatchingSOC={state.requireMatchingSOC}
      onRequireMatchingSOCChange={(value) =>
        dispatch({ type: "SET_REQUIRE_MATCHING_SOC", payload: value })
      }
      onEdgeRatingChange={handleEdgeRatingChange}
      activeTab={activeTab}
      onTabChange={(tab) =>
        dispatch({ type: "SET_ACTIVE_TAB", payload: tab })
      }
      revealSeq={revealSeq}
      hoverElement={hoverState.element}
      onElementHover={handleElementHover}
      hoverHour={hoverState.hoverHour}
      onHourHover={handleHourHover}
      numTimeSteps={numTimeSteps}
      onNumTimeStepsChange={(value) =>
        dispatch({ type: "SET_NUM_TIME_STEPS", payload: value })
      }
      selectedBusId={selectedBusId}
      onBusSelect={handleSelectNode}
      mobile={mobile}
      onExit={onExit}
    />
  );

  const importExportDialog = (
    <ImportExportDialog
      isOpen={showImportExport}
      importExportText={importExportText}
      onTextChange={(text) =>
        dispatch({ type: "SET_IMPORT_EXPORT_TEXT", payload: text })
      }
      onClose={() =>
        dispatch({ type: "SET_SHOW_IMPORT_EXPORT", payload: false })
      }
      onImport={handleImport}
    />
  );

  // --- Mobile layout: one screen per level of the component tree -------------
  if (isMobile) {
    return (
      <>
        <MobileApp
          toolbar={toolbarEl}
          network={
            <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
              {networkEl}
            </div>
          }
          charts={
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                background: "white",
              }}
            >
              {chartsEl}
            </div>
          }
          renewables={
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              {renewablesEl}
            </div>
          }
          renderElements={(onExit) => renderElementTable(true, onExit)}
        />
        {importExportDialog}
      </>
    );
  }

  // --- Desktop layout: 2×2 quadrant grid ------------------------------------
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f0f0f0",
      }}
    >
      {/* Top Toolbar */}
      {toolbarEl}

      {/* Main Grid */}
      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr",
          gap: "4px",
          padding: "4px",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Top Left: Network Visualization */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "4px",
            overflow: "hidden",
            position: "relative",
            minHeight: 0,
            height: "100%",
          }}
        >
          {networkEl}
        </div>

        {/* Top Right: Load Curve, Battery SOC, and LMP Curve (scrollable) */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "4px",
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {chartsEl}
        </div>

        {/* Bottom Left: Tables */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "4px",
            overflow: "hidden",
            minHeight: 0,
            height: "100%",
          }}
        >
          {renderElementTable(false)}
        </div>

        {/* Bottom Right: Wind and Sun Curves */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "4px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            height: "100%",
          }}
        >
          {renewablesEl}
        </div>
      </div>

      {/* Import/Export Dialog */}
      {importExportDialog}
    </div>
  );
}

export default App;
